/**
 * HeartAffirmations — Preview heart-specific affirmation categories
 * Categories renamed to poetic "pathway" names
 * Glow on expand for activation feel
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  safety_trust: { label: 'Softening into Safety', description: 'Feeling safe to soften' },
  emotional_release: { label: 'Letting Go Gently', description: 'Releasing what no longer serves' },
  warmth_connection: { label: 'Being Held', description: 'Warmth, love, connection' },
  opening_receiving: { label: 'Allowing In', description: 'Opening to what is good' },
  integration: { label: 'Trusting the Heart', description: 'The heart knows how to heal' },
};

const CATEGORY_ORDER = ['safety_trust', 'emotional_release', 'warmth_connection', 'opening_receiving', 'integration'];

const HeartAffirmations = ({ onContinue }) => {
  const [categories, setCategories] = useState({});
  const [expandedCat, setExpandedCat] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAffirmations = async () => {
      try {
        const res = await fetch(`${API}/api/healing/affirmations/heart-flow`);
        const data = await res.json();
        if (data.categories) setCategories(data.categories);
      } catch (e) {
        console.error('Failed to load affirmations:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchAffirmations();
  }, []);

  const toggleExpand = (cat) => {
    setExpandedCat(prev => prev === cat ? null : cat);
  };

  const allAffirmations = CATEGORY_ORDER.flatMap(cat => categories[cat] || []);

  return (
    <motion.div
      className="fixed inset-0 flex flex-col"
      style={{ background: 'radial-gradient(ellipse at 50% 40%, rgba(30,22,14,1) 0%, rgba(10,7,4,1) 65%)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.5 }}
      data-testid="heart-affirmation-selection"
    >
      <div className="flex-1 flex flex-col items-center pt-16 px-6 overflow-y-auto pb-32">
        <motion.h2
          className="text-2xl text-white/70 text-center mb-2"
          style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          Heart Affirmations
        </motion.h2>
        <motion.p
          className="text-white/25 text-xs text-center mb-8 max-w-xs"
          style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: '0.03em' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          These will be whispered gently during your immersion
        </motion.p>

        {loading ? (
          <p className="text-white/20 text-sm">Loading...</p>
        ) : (
          <div className="w-full max-w-sm space-y-3">
            {CATEGORY_ORDER.map((catKey, i) => {
              const meta = CATEGORY_META[catKey];
              const affirmations = categories[catKey] || [];
              const isExpanded = expandedCat === catKey;

              return (
                <motion.div
                  key={catKey}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + i * 0.1 }}
                >
                  <motion.button
                    onClick={() => toggleExpand(catKey)}
                    className="w-full text-left px-4 py-3 rounded-xl relative overflow-hidden"
                    style={{
                      background: isExpanded ? 'rgba(200,165,115,0.1)' : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${isExpanded ? 'rgba(200,165,115,0.2)' : 'rgba(255,255,255,0.04)'}`,
                    }}
                    data-testid={`heart-cat-${catKey}`}
                  >
                    {/* Glow when expanded — activation feel */}
                    {isExpanded && (
                      <motion.div
                        className="absolute inset-0 pointer-events-none rounded-xl"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        style={{
                          background: 'radial-gradient(ellipse at 50% 50%, rgba(210,175,130,0.08) 0%, transparent 70%)',
                        }}
                      />
                    )}
                    <div className="flex items-center justify-between relative z-10">
                      <div>
                        <p className="text-white/55 text-sm" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
                          {meta.label}
                        </p>
                        <p className="text-white/20 text-xs mt-0.5" style={{ fontFamily: "'Manrope', sans-serif" }}>
                          {meta.description}
                        </p>
                      </div>
                      <span className="text-white/20 text-xs">{affirmations.length}</span>
                    </div>
                  </motion.button>

                  <AnimatePresence>
                    {isExpanded && affirmations.length > 0 && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 py-2 space-y-1.5">
                          {affirmations.map((aff, j) => (
                            <p key={j} className="text-white/30 text-xs leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                              {aff}
                            </p>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="fixed bottom-0 left-0 right-0 pb-10 pt-6 flex justify-center" style={{
        background: 'linear-gradient(180deg, transparent 0%, rgba(10,7,4,0.95) 40%)',
      }}>
        <motion.button
          onClick={() => onContinue(allAffirmations)}
          className="px-10 py-3.5 rounded-full text-white/55 text-sm"
          style={{
            background: 'rgba(200,165,115,0.1)',
            border: '1px solid rgba(200,165,115,0.18)',
            fontFamily: "'Manrope', sans-serif",
            letterSpacing: '0.03em',
          }}
          whileTap={{ scale: 0.97 }}
          data-testid="heart-affirmation-continue-btn"
        >
          Begin immersion
        </motion.button>
      </div>
    </motion.div>
  );
};

export default HeartAffirmations;
